import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Send,
  AlertCircle,
  CheckCircle2,
  Upload,
  FileText,
  Briefcase,
  Trash2,
} from 'lucide-react';
import { Button } from './Button';
import { PhoneInput } from './PhoneInput';
import { composePhoneNumber, DEFAULT_PHONE_VALUE, type PhoneValue } from './phoneUtils';
import { OtpModal } from './OtpModal';
import { sendOtp } from '../../services/contactApi';
import {
  applyForRole,
  validateResume,
  ALLOWED_RESUME_EXTENSIONS,
} from '../../services/careerApi';

interface ApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * If provided, the role is locked to this value (the "Apply Now" case
   * on a specific job posting). If omitted, the modal renders a role selector
   * so the applicant can pick from `roleOptions` or type their own.
   */
  role?: string;
  department?: string;
  /** Role suggestions shown when no `role` is preset. Ignored otherwise. */
  roleOptions?: string[];
}

interface ApplyFormState {
  roleSelection: string;
  customRole: string;
  name: string;
  personalEmail: string;
  workExperience: string;
  currentCompany: string;
  phone: PhoneValue;
  message: string;
  attachment: File | null;
}

const INITIAL_FORM: ApplyFormState = {
  roleSelection: '',
  customRole: '',
  name: '',
  personalEmail: '',
  workExperience: '',
  currentCompany: '',
  phone: DEFAULT_PHONE_VALUE,
  message: '',
  attachment: null,
};

const ACCEPT_ATTR = ALLOWED_RESUME_EXTENSIONS.map((e) => `.${e}`).join(',');
const OTHER_ROLE_VALUE = '__other__';

function resolveRole(form: ApplyFormState, presetRole?: string): string {
  if (presetRole && presetRole.trim()) return presetRole.trim();
  if (form.roleSelection === OTHER_ROLE_VALUE) return form.customRole.trim();
  return form.roleSelection.trim();
}

export const ApplyModal: React.FC<ApplyModalProps> = ({
  isOpen,
  onClose,
  role,
  department,
  roleOptions,
}) => {
  const [formData, setFormData] = useState<ApplyFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const showRoleSelector = !role || !role.trim();

  useEffect(() => {
    if (isOpen) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setApiError('');
      setOtpVerifyError('');
      setIsSubmitted(false);
    }
  }, [isOpen]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (showRoleSelector) {
      if (!formData.roleSelection) {
        newErrors.roleSelection = 'Please select a role';
      } else if (formData.roleSelection === OTHER_ROLE_VALUE && !formData.customRole.trim()) {
        newErrors.customRole = 'Please type the role you are applying for';
      }
    }

    if (!formData.name.trim()) newErrors.name = 'Name is required';

    if (!formData.personalEmail.trim()) {
      newErrors.personalEmail = 'Personal email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalEmail)) {
      newErrors.personalEmail = 'Please enter a valid email address';
    }

    if (!formData.workExperience.trim()) {
      newErrors.workExperience = 'Work experience is required';
    }

    if (!formData.currentCompany.trim()) {
      newErrors.currentCompany = 'Current company is required';
    }

    if (!formData.phone.number.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'A short cover message is required';
    }

    if (!formData.attachment) {
      newErrors.attachment = 'Please attach your resume';
    } else {
      const result = validateResume(formData.attachment);
      if (!result.ok) newErrors.attachment = result.message;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'roleSelection' && value !== OTHER_ROLE_VALUE) {
        next.customRole = '';
      }
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'roleSelection' && errors.customRole) {
      setErrors((prev) => ({ ...prev, customRole: '' }));
    }
    if (apiError) setApiError('');
  };

  const handlePhoneChange = (value: PhoneValue) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFormData((prev) => ({ ...prev, attachment: file }));
    if (errors.attachment) setErrors((prev) => ({ ...prev, attachment: '' }));
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, attachment: null }));
  };

  // Step 1: Send OTP to personal_email
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.personalEmail.trim(),
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)
          ?.retry_after_seconds;
        setApiError(
          retryAfter
            ? `Please wait ${retryAfter} seconds before requesting another OTP.`
            : response.message,
        );
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Verify OTP + submit application
  const handleOtpVerify = useCallback(
    async (otp: string) => {
      if (!formData.attachment) {
        setOtpVerifyError('Missing resume. Please close and re-attach.');
        return;
      }
      setOtpVerifyError('');
      setIsVerifyingOtp(true);
      try {
        const response = await applyForRole({
          name: formData.name.trim(),
          personal_email: formData.personalEmail.trim(),
          work_experience: formData.workExperience.trim(),
          role: resolveRole(formData, role),
          current_company: formData.currentCompany.trim(),
          phone_number: composePhoneNumber(formData.phone),
          message: formData.message.trim(),
          otp,
          attachment: formData.attachment,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(response.message || 'Submission failed. Please try again.');
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    [formData, role],
  );

  const handleResendOtp = useCallback(async () => {
    const response = await sendOtp({
      name: formData.name.trim(),
      email: formData.personalEmail.trim(),
    });
    if (response.status === 'success') {
      return { success: true };
    }
    if (response.status_code === 429) {
      const retryAfter = (response.data as { retry_after_seconds?: number } | null)
        ?.retry_after_seconds;
      setOtpVerifyError(response.message);
      return { success: false, retryAfter: retryAfter || 60 };
    }
    setOtpVerifyError(response.message || 'Failed to resend OTP.');
    return { success: false };
  }, [formData.name, formData.personalEmail]);

  return (
    <>
      <OtpModal
        isOpen={showOtpModal}
        email={formData.personalEmail}
        onClose={() => {
          setShowOtpModal(false);
          setOtpVerifyError('');
        }}
        onVerify={handleOtpVerify}
        onResend={handleResendOtp}
        verifyError={otpVerifyError}
        isVerifying={isVerifyingOtp}
      />

      <AnimatePresence>
        {isOpen && !showOtpModal && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />

            <motion.div
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90dvh] overflow-hidden flex flex-col"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-lg text-navy-400 hover:text-navy-700 hover:bg-navy-100 transition-colors z-10"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="bg-gradient-to-br from-navy-900 to-navy-800 px-5 sm:px-8 pt-8 pb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-accent-300" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {role ? `Apply for ${role}` : 'Apply for a Role'}
                    </h3>
                    {department && (
                      <p className="text-navy-300 text-sm">{department} · ISTATEC</p>
                    )}
                  </div>
                </div>
                <p className="text-navy-200 text-sm">
                  Fill in your details below. We'll email a 4-digit verification code to your personal
                  email to confirm your identity before submitting.
                </p>
              </div>

              {/* Body */}
              <div className="overflow-y-auto flex-1 px-5 sm:px-8 py-6">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h4 className="text-xl font-bold text-navy-900 mb-2">Application Submitted</h4>
                    <p className="text-navy-600 mb-6">
                      Thank you for applying to{' '}
                      <span className="font-medium">{resolveRole(formData, role) || 'ISTATEC'}</span>
                      . Our team will review your resume and get back to you soon.
                    </p>
                    <Button variant="outline" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {apiError && (
                      <motion.div
                        className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600">{apiError}</p>
                      </motion.div>
                    )}

                    {showRoleSelector && (
                      <div>
                        <label
                          htmlFor="apply-role"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Role You're Applying For *
                        </label>
                        <select
                          id="apply-role"
                          name="roleSelection"
                          value={formData.roleSelection}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            errors.roleSelection
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all bg-white`}
                        >
                          <option value="">Select a role</option>
                          {(roleOptions ?? []).map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                          <option value={OTHER_ROLE_VALUE}>Other — type your own</option>
                        </select>
                        {errors.roleSelection && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.roleSelection}
                          </p>
                        )}
                        {formData.roleSelection === OTHER_ROLE_VALUE && (
                          <div className="mt-2">
                            <input
                              type="text"
                              id="customRole"
                              name="customRole"
                              value={formData.customRole}
                              onChange={handleChange}
                              autoFocus
                              className={`w-full px-4 py-2.5 rounded-lg border ${
                                errors.customRole
                                  ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                              } focus:ring-2 outline-none transition-all`}
                              placeholder="e.g. Staff Backend Engineer"
                            />
                            {errors.customRole && (
                              <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {errors.customRole}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="apply-name"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Full Name *
                        </label>
                        <input
                          id="apply-name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            errors.name
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all`}
                          placeholder="Aarav Sharma"
                        />
                        {errors.name && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="apply-email"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Personal Email *
                        </label>
                        <input
                          id="apply-email"
                          type="email"
                          name="personalEmail"
                          value={formData.personalEmail}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            errors.personalEmail
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all`}
                          placeholder="you@gmail.com"
                        />
                        {errors.personalEmail && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.personalEmail}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="apply-experience"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Work Experience *
                        </label>
                        <input
                          id="apply-experience"
                          type="text"
                          name="workExperience"
                          value={formData.workExperience}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            errors.workExperience
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all`}
                          placeholder="e.g. 5 years"
                        />
                        {errors.workExperience && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.workExperience}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="apply-company"
                          className="block text-sm font-medium text-navy-700 mb-2"
                        >
                          Current Company *
                        </label>
                        <input
                          id="apply-company"
                          type="text"
                          name="currentCompany"
                          value={formData.currentCompany}
                          onChange={handleChange}
                          className={`w-full px-4 py-2.5 rounded-lg border ${
                            errors.currentCompany
                              ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                              : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                          } focus:ring-2 outline-none transition-all`}
                          placeholder="Acme Corp"
                        />
                        {errors.currentCompany && (
                          <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {errors.currentCompany}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="apply-phone"
                        className="block text-sm font-medium text-navy-700 mb-2"
                      >
                        Phone Number *
                      </label>
                      <PhoneInput
                        id="apply-phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        hasError={Boolean(errors.phone)}
                      />
                      {errors.phone && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.phone}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="apply-message"
                        className="block text-sm font-medium text-navy-700 mb-2"
                      >
                        Cover Message *
                      </label>
                      <textarea
                        id="apply-message"
                        name="message"
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-2.5 rounded-lg border ${
                          errors.message
                            ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-navy-200 focus:border-accent-500 focus:ring-accent-500/20'
                        } focus:ring-2 outline-none transition-all resize-none`}
                        placeholder={`Why are you a great fit for the ${role} role?`}
                      />
                      {errors.message && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-navy-700 mb-2">
                        Resume / CV *
                      </label>
                      {formData.attachment ? (
                        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-navy-200 bg-navy-50">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="w-5 h-5 text-accent-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-navy-900 truncate">
                                {formData.attachment.name}
                              </p>
                              <p className="text-xs text-navy-500">
                                {(formData.attachment.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveFile}
                            className="p-1.5 rounded-md text-navy-500 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                            aria-label="Remove file"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="apply-attachment"
                          className={`flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                            errors.attachment
                              ? 'border-red-300 bg-red-50/50 hover:bg-red-50'
                              : 'border-navy-200 bg-navy-50 hover:bg-navy-100'
                          }`}
                        >
                          <Upload className="w-6 h-6 text-navy-500" />
                          <div className="text-center">
                            <p className="text-sm font-medium text-navy-700">
                              Click to upload your resume
                            </p>
                            <p className="text-xs text-navy-500 mt-1">
                              {ALLOWED_RESUME_EXTENSIONS.join(', ')} · up to 10 MB
                            </p>
                          </div>
                          <input
                            id="apply-attachment"
                            type="file"
                            accept={ACCEPT_ATTR}
                            onChange={handleFileChange}
                            className="hidden"
                          />
                        </label>
                      )}
                      {errors.attachment && (
                        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.attachment}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className="sm:w-auto w-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        rightIcon={<Send className="w-4 h-4" />}
                        className="sm:flex-1 w-full"
                      >
                        Submit Application
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
